import { useEffect, useRef, useState } from 'react'
import { Button, ButtonLink, Form, Notice, TextField } from '@/components/play'
import { authLink } from '@/components/onboarding/authContinuation'
import { retryWait } from '@/lib/retryWait'
import { api } from '@/util/api'

type LinkRequestError = {
  response?: { status?: number; data?: { error?: { message?: string; type?: string; errors?: { email?: string[] } } } }
}

const PROVIDER_NAMES: Record<string, string> = { strava: 'Strava', google: 'Google' }
const LINK_FAILED = "We couldn't send a link. Try again."
const LINK_OFFLINE = "We couldn't send a link. Check your connection and try again."
const TOO_MANY = 'Too many requests. Try again shortly.'

/**
 * A first sign-in with a provider that shares no email address (Strava never does): asks for one, saying why, and
 * emails a sign-in link that carries the provider's sealed identity (`pending`). Opening the link creates the account,
 * or signs in the one with that address, and links the provider, so the next sign-in with it goes straight through.
 */
export function OAuthEmailStep({ provider, pending, returnTo }: { provider: string; pending: string; returnTo: string }) {
  const name = PROVIDER_NAMES[provider] ?? 'This provider'
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [sending, setSending] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const sentNotice = useRef<HTMLDivElement>(null)
  const emailInput = useRef<HTMLInputElement>(null)
  const changing = useRef(false)

  useEffect(() => {
    if (sentTo) sentNotice.current?.focus()
    else if (changing.current) {
      changing.current = false
      emailInput.current?.select()
    }
  }, [sentTo])

  async function sendLink(event: React.FormEvent) {
    event.preventDefault()
    if (sending) return
    setError(null)
    setEmailError(null)
    const address = email.trim()
    if (!address) {
      setEmailError('Enter your email address.')
      return
    }
    setSending(true)
    try {
      await api.post('/auth/magic_link', { email: address, return_to: returnTo, oauth_link: pending })
      setSentTo(address)
    } catch (err) {
      const response = (err as LinkRequestError)?.response
      const serverError = response?.data?.error
      if (!response) setError(LINK_OFFLINE)
      else if (response.status === 422 && serverError?.type === 'oauth_link_expired') {
        setExpired(true)
        setError(serverError.message ?? `Your ${name} sign-in has expired. Continue with ${name} again.`)
      } else if (response.status === 422) {
        setEmailError(serverError?.errors?.email?.[0] || serverError?.message || 'Enter a valid email address.')
      } else if (response.status === 429) {
        const wait = retryWait(err)
        setError(wait ? `Too many link requests. Try again in ${wait}.` : serverError?.message || TOO_MANY)
      } else setError(serverError?.message || LINK_FAILED)
    } finally {
      setSending(false)
    }
  }

  if (sentTo) {
    return (
      <div className="space-y-5">
        <Notice ref={sentNotice} tabIndex={-1} tone="success" title="Check your email">
          A sign-in link is on its way to <strong>{sentTo}</strong>. Open it to finish signing in with {name}.
        </Notice>
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          onClick={() => {
            changing.current = true
            setSentTo(null)
          }}
        >
          Use a different email
        </Button>
      </div>
    )
  }

  if (expired) {
    return (
      <div className="space-y-5">
        <Notice tone="error" alert>
          {error}
        </Notice>
        <ButtonLink to={authLink('/login', returnTo)} className="w-full">
          Back to sign in
        </ButtonLink>
      </div>
    )
  }

  return (
    <Form onSubmit={sendLink}>
      <Notice title="Add your email">
        {name} doesn’t share your email address, so we’re asking for it. We’ll send you a link to confirm it and finish
        signing in.
      </Notice>
      {error && (
        <Notice tone="error" alert>
          {error}
        </Notice>
      )}
      <TextField
        ref={emailInput}
        label="Email"
        id="oauth-email"
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(event) => {
          setEmail(event.target.value)
          setEmailError(null)
        }}
        error={emailError ?? undefined}
        required
        requiredMessage="Enter your email address."
        autoFocus
        disabled={sending}
      />
      <Button type="submit" className="w-full" loading={sending}>
        Email me a link
      </Button>
    </Form>
  )
}
